import { useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import type { BarCustomLayerProps } from '@nivo/bar';
import type { GeoBrowseEnrichmentRow } from '../../types';
import styles from '../../GeoBrowse.module.css';

const CHART_THEME = {
  axis: {
    ticks: { text: { fontSize: 10, fill: '#6b7280' } },
    legend: { text: { fontSize: 11, fill: '#374151', fontWeight: 500 } },
  },
  grid: { line: { stroke: '#eef2f7', strokeWidth: 1 } },
};

const ENRICHED_COLOR = '#e64b35';
const DEPLETED_COLOR = '#4dbbd5';

type EnrichmentBarChartProps = {
  rows: GeoBrowseEnrichmentRow[];
  collection: string;
  fdr: number;
};

type ChartRow = {
  id: string;
  label: string;
  fullName: string;
  nes: number;
  fdr_qval: number;
  tag_pct: number;
  gene_pct: number;
  color: string;
};

function truncateLabel(name: string, max = 34): string {
  if (name.length <= max) return name;
  return `${name.slice(0, max - 1)}…`;
}

function RowLabels({
  bars,
  labelById,
  fullNameById,
}: Pick<BarCustomLayerProps<ChartRow>, 'bars'> & {
  labelById: Record<string, string>;
  fullNameById: Record<string, string>;
}) {
  return (
    <g pointerEvents="none">
      {bars.map(bar => {
        const id = String(bar.data.indexValue);
        const label = labelById[id] ?? id;
        const y = bar.y + bar.height / 2;

        return (
          <text
            key={bar.key}
            x={-10}
            y={y}
            textAnchor="end"
            dominantBaseline="central"
            style={{ fontSize: 10, fill: '#374151', fontWeight: 500 }}
            stroke="#fff"
            strokeWidth={3}
            paintOrder="stroke"
          >
            <title>{fullNameById[id] ?? label}</title>
            {label}
          </text>
        );
      })}
    </g>
  );
}

export default function EnrichmentBarChart({ rows, collection, fdr }: EnrichmentBarChartProps) {
  const chartData = useMemo<ChartRow[]>(
    () =>
      [...rows]
        .sort((a, b) => b.nes - a.nes)
        .map(row => ({
          id: row.gene_set_id,
          label: truncateLabel(row.gene_set_name),
          fullName: row.gene_set_name,
          nes: row.nes,
          fdr_qval: row.fdr_qval,
          tag_pct: row.tag_pct,
          gene_pct: row.gene_pct,
          color: row.nes >= 0 ? ENRICHED_COLOR : DEPLETED_COLOR,
        })),
    [rows],
  );

  const labelById = useMemo(
    () => Object.fromEntries(chartData.map(row => [row.id, row.label])),
    [chartData],
  );

  const fullNameById = useMemo(
    () => Object.fromEntries(chartData.map(row => [row.id, row.fullName])),
    [chartData],
  );

  const nesExtent = useMemo(() => {
    const absMax = Math.max(...chartData.map(row => Math.abs(row.nes)), 1);
    const pad = absMax * 0.12;
    return { min: -(absMax + pad), max: absMax + pad };
  }, [chartData]);

  const leftMargin = useMemo(() => {
    const longest = Math.max(...chartData.map(row => row.label.length), 12);
    return Math.min(260, Math.max(200, longest * 6.2 + 24));
  }, [chartData]);

  const chartHeight = Math.max(380, chartData.length * 24 + 56);

  if (!chartData.length) {
    return <div className={styles.tabEmpty}>No enrichment results at current thresholds.</div>;
  }

  const title = `${collection} — top enriched (NES>0, red) / depleted (NES<0, blue), FDR q<${fdr}`;

  return (
    <div className={styles.enrichmentChartBlock}>
      <h3 className={styles.enrichmentChartTitle}>{title}</h3>
      <div className={styles.enrichmentChartArea} style={{ height: chartHeight }}>
        <ResponsiveBar
          data={chartData}
          keys={['nes']}
          indexBy="id"
          layout="horizontal"
          margin={{ top: 12, right: 24, bottom: 44, left: leftMargin }}
          padding={0.32}
          innerPadding={2}
          valueScale={{ type: 'linear', min: nesExtent.min, max: nesExtent.max }}
          colors={bar => String(bar.data.color)}
          theme={CHART_THEME}
          enableGridX
          gridXValues={[0]}
          markers={[
            {
              axis: 'x',
              value: 0,
              lineStyle: { stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '4 4' },
            },
          ]}
          axisBottom={{
            legend: 'Normalized Enrichment Score (NES)',
            legendOffset: 36,
            legendPosition: 'middle',
            tickSize: 0,
            tickPadding: 6,
          }}
          axisLeft={{
            tickSize: 0,
            renderTick: () => <g />,
          }}
          enableLabel={false}
          borderRadius={2}
          layers={[
            'grid',
            'axes',
            'bars',
            'markers',
            props => <RowLabels {...props} labelById={labelById} fullNameById={fullNameById} />,
          ]}
          tooltip={({ indexValue, value, data: barData }) => (
            <div className={styles.volcanoTooltip}>
              <strong>{String(barData.fullName ?? indexValue)}</strong>
              <div>NES: {Number(value).toFixed(2)}</div>
              <div>FDR q-val: {Number(barData.fdr_qval).toFixed(3)}</div>
              <div>
                hits: {Number(barData.tag_pct).toFixed(4)} / {Number(barData.gene_pct).toFixed(4)}
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
}
